import type {GetServerSideProps, InferGetStaticPropsType, NextPage,} from "next";
import CrowlynHead from "@components/crowlyn-head/crowlyn-head";
import {motion} from 'framer-motion';
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {useTranslation} from "next-i18next";
import {Header} from "@components/sections/header/header";
import {Footer} from "@components/sections/footer/footer";
import {BannerObjectType} from "../@types/types-header";
import {useRouter} from "next/router";
import {i18translateType} from "../@types/types-body";
import dynamic from "next/dynamic";
import {TitleGreenSpanSecond} from "@components/title/title-green-span-second";
import React, {useState} from "react";

type CustomWebsitePageProps = InferGetStaticPropsType<typeof getServerSideProps>;

const CustomWebFaq = dynamic(() => import("@components/sections/services/custom-web/custom-web-faq"), {
    suspense: true,
});
const WeLoveTechnology = dynamic(() => import("@components/sections/technologies/we-love-technology"), {
    suspense: true,
});
const CustomWebsitePage: NextPage<CustomWebsitePageProps> = (server_side_props: any) => {
    const namespace = "custom";
    const {t}: i18translateType = useTranslation(namespace);
    const router = useRouter();
    const bannerObject: BannerObjectType = {
        backgroundImage: "/images/banners/web-banner.webp", buttonLink: t("common:menu.path.contact"),
    };
    const [activeTab, setActiveTab] = useState<string>("tailored-websites")
    return (<>
        <CrowlynHead
            description={t("seo_description")}
            title={t("seo_title")}
            keywords={t("seo_keywords")}
            lang={server_side_props.locale ?? router.locale}
            path={router.pathname}
            englishPath={"/tailored-applications"}
            germanPath={"/benutzerdefinierte-web-applikationen"}
            image={"/images/banners/web-banner.webp"}
        />
        <Header banner={bannerObject} namespace={namespace}/>
        <div className={"container mx-auto mt-12"}>
            <div className="flex flex-wrap justify-center my-4">
                <button onClick={() => setActiveTab('tailored-websites')}
                        className={`${activeTab === "tailored-websites" ? 'bg-crowlyn' : 'bg-crowlyn-button'} hover:bg-crowlyn hover:text-white text-white rounded-xl text-base sm:text-xl md:text-base px-3 py-2 my-2 mx-2 text-center`}>
                    Tailored Websites
                </button>
                <button onClick={() => setActiveTab('tailored-e-commerce')}
                        className={`${activeTab === "tailored-e-commerce" ? 'bg-crowlyn' : 'bg-crowlyn-button'} hover:bg-crowlyn hover:text-white text-white rounded-xl text-base sm:text-xl md:text-base px-3 py-2 my-2 mx-2 text-center`}>
                    Tailored E-commerce
                </button>
                <button onClick={() => setActiveTab('tailored-software')}
                        className={`${activeTab === "tailored-software" ? 'bg-crowlyn' : 'bg-crowlyn-button'} hover:bg-crowlyn hover:text-white text-white rounded-xl text-base sm:text-xl md:text-base px-3 py-2 my-2 mx-2 text-center`}>
                    Tailored Software
                </button>
            </div>
            <div className="w-full bg-white rounded-lg shadow-2xl py-12">
                {activeTab === "tailored-websites" && <motion.div
                    className="my-12"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{
                        duration: 2, ease: "easeIn"
                    }}
                    id="tailored-websites">
                    <TitleGreenSpanSecond
                        id="section-why-us-custom-web-title"
                        namespace={namespace}
                        title="tailored.websites.title"
                        span="tailored.websites.span"
                        description="tailored.websites.text"
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-2 px-5 mt-8 gap-12">
                        <div className="w-full text-justify">
                            <h3 className="text-2xl font-bold tracking-tight text-gray-900">{t('tailored.websites.service.title')}</h3>
                            <p className="mb-1 text-gray-500">{t('tailored.websites.service.blog_pages')}</p>
                            <p className="mb-1 text-gray-500">{t('tailored.websites.service.business_websites')}</p>
                        </div>
                        <div className="w-full text-justify">
                            <h4 className="text-2xl font-bold tracking-tight text-gray-900">{t('tailored.websites.process.title')}</h4>
                            <p className="my-1 text-gray-500">{t('tailored.websites.process.consultation')}</p>
                            <p className="my-1 text-gray-500">{t('tailored.websites.process.design_development')}</p>
                            <p className="my-1 text-gray-500">{t('tailored.websites.process.testing_lunch')}</p>
                            <p className="my-1 text-gray-500">{t('tailored.websites.process.support_maintanance')}</p>
                        </div>
                    </div>
                </motion.div>}
                {activeTab === "tailored-e-commerce" && <motion.div
                    className="my-12"
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{
                        duration: 2, ease: "easeIn"
                    }}
                    id="tailored-e-commerce">
                    <TitleGreenSpanSecond
                        id="section-why-us-custom-web-title"
                        namespace={namespace}
                        title="tailored.e_commerce.title"
                        span="tailored.e_commerce.span"
                        description="tailored.e_commerce.text"
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-2 px-5 mt-8 gap-12">
                        <div className="w-full text-justify">
                            <h3 className="mt-6 mb-2 text-2xl font-bold tracking-tight text-gray-900">{t('tailored.e_commerce.service.title')}</h3>
                            <p className="mb-1 text-gray-500">{t('tailored.e_commerce.service.custom_store')}</p>
                            <p className="mb-1 text-gray-500">{t('tailored.e_commerce.service.integrations_service')}</p>
                        </div>
                        <div className="w-full text-justify justify-center">
                            <h5 className="mt-6 mb-2 text-xl font-bold tracking-tight text-gray-900">{t('tailored.e_commerce.process.title')}</h5>
                            <p className="my-1 text-gray-500">{t('tailored.e_commerce.process.e_commerce_strategy')}</p>
                            <p className="mb-1 text-gray-500">{t('tailored.e_commerce.process.design_development')}</p>
                            <p className="mb-1 text-gray-500">{t('tailored.e_commerce.process.testing_optimization')}</p>
                            <p className="mb-1 text-gray-500">{t('tailored.e_commerce.process.launch_and_growth')}</p>
                        </div>
                    </div>
                </motion.div>}
                {activeTab === "tailored-software" && <motion.div
                    className="my-12"
                     initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{
                        duration: 2, ease: "easeIn"
                    }} id="tailored-software">
                    <TitleGreenSpanSecond
                        id="section-why-us-custom-web-title"
                        namespace={namespace}
                        title="tailored.software.title"
                        span="tailored.software.span"
                        description="tailored.software.text"
                    />
                    <div className="grid grid-cols-1 lg:grid-cols-2 px-5 mt-8 gap-12">
                        <div className="w-full text-justify">
                            <h3 className="text-2xl font-bold tracking-tight text-gray-900">{t('tailored.software.example.title')}</h3>
                            <p className="mb-1 text-gray-500">{t('tailored.software.example.challenge')}</p>
                            <p className="mb-1 text-gray-500">{t('tailored.software.example.solution')}</p>
                        </div>
                        <div className="w-full text-justify">
                            <h4 className="text-2xl font-bold tracking-tight text-gray-900">{t('tailored.software.process.title')}</h4>
                            <p className="my-1 text-gray-500">{t('tailored.software.process.analysis')}</p>
                            <p className="my-1 text-gray-500">{t('tailored.software.process.solution_design')}</p>
                            <p className="my-1 text-gray-500">{t('tailored.software.process.development_testing')}</p>
                            <p className="my-1 text-gray-500">{t('tailored.software.process.deployment_and_support')}</p>
                        </div>
                    </div>
                </motion.div>}
            </div>
        </div>
        <WeLoveTechnology namespace={namespace}/>
        <CustomWebFaq namespace={namespace}/>
        <Footer/>
    </>)

};

export const getServerSideProps: GetServerSideProps = async (context) => {
    return {
        props: {
            ...(await serverSideTranslations(context.locale as string, ["common", "custom"])),
        },
    };
};
export default CustomWebsitePage;
